import type { AutocompleteChangeReason, AutocompleteProps } from '@mui/material';
import type { GridRenderEditCellParams } from '@mui/x-data-grid-premium';
import type { Predicate, SearchDescriptor } from '@shared/core/search';
import { type ReactNode, useCallback, useMemo, useState } from 'react';

import AutoCompleteCell from 'src/components/atoms/grid/AutoCompleteCell';
import { type SearchCellValue, useSearchCellControls } from 'src/hooks/useSearchCellControls';

import { useSearch } from '../hooks/useSearch';
import { searchStateText } from './searchStateText';

export type EntitySearchCellProps<TDoc extends object, V> = GridRenderEditCellParams & {
  descriptor: SearchDescriptor<TDoc>;
  filter?: Predicate<TDoc>;
  getOptionLabel: (option: TDoc | V) => string;
  getOptionKey: (option: TDoc | V) => string | number;
  isOptionEqualToValue: (option: TDoc | V, value: TDoc | V) => boolean;
  transform?: (doc: TDoc) => V | null;
  filterOptions?: (options: (TDoc | V)[], params: { inputValue: string }) => (TDoc | V)[];
  details?: (option: TDoc, inputValue: string) => ReactNode;
  defaultSearchText?: string;
  noOptionsText?: ReactNode;
  onChange?: (event: React.SyntheticEvent, value: SearchCellValue<TDoc, V>, reason: AutocompleteChangeReason) => void;
} & Omit<
    AutocompleteProps<any, any, any, any>,
    | 'renderInput'
    | 'id'
    | 'options'
    | 'onChange'
    | 'getOptionLabel'
    | 'getOptionKey'
    | 'noOptionsText'
    | 'isOptionEqualToValue'
    | 'filterOptions'
    | 'defaultValue'
    | 'value'
  >;

const isChangeReason = (reason: string): reason is AutocompleteChangeReason =>
  reason === 'blur' ||
  reason === 'clear' ||
  reason === 'createOption' ||
  reason === 'removeOption' ||
  reason === 'selectOption';

const passThrough = <T,>(options: T[]): T[] => options;

type CellProps<TDoc, V> = React.ComponentProps<typeof AutoCompleteCell<TDoc | V>>;

export function EntitySearchCell<TDoc extends object, V>({
  descriptor,
  filter,
  transform,
  getOptionKey,
  defaultSearchText,
  noOptionsText,
  onChange,
  filterOptions = passThrough,
  ...props
}: EntitySearchCellProps<TDoc, V>) {
  const [query, setQuery] = useState('');

  const handleControlsChange = useMemo(
    () =>
      onChange
        ? (event: React.SyntheticEvent, next: SearchCellValue<TDoc, V>, reason: string) => {
            if (isChangeReason(reason)) onChange(event, next, reason);
          }
        : undefined,
    [onChange]
  );

  const { open, setOpen, handleFocus, handleBlur, handleOpen, handleChange, handleSearch } = useSearchCellControls<
    TDoc,
    V
  >({
    id: props.id,
    field: props.field,
    value: props.value,
    getOptionLabel: props.getOptionLabel,
    transform,
    onChange: handleControlsChange,
    search: setQuery,
    hasFocus: props.hasFocus,
    multiple: props.multiple,
    disableCloseOnSelect: props.disableCloseOnSelect,
    openOnFocus: props.openOnFocus,
    isOptionEqualToValue: props.isOptionEqualToValue,
    defaultSearchText,
  });

  const { hits, state } = useSearch({ descriptor, query, filter, enabled: open });
  const loading = open && state === 'loading';

  const stateText = useMemo(() => searchStateText(state, noOptionsText), [state, noOptionsText]);

  const options = useMemo(() => (loading || state === 'forbidden' ? [] : [...hits]), [loading, state, hits]);

  const handleClose = useCallback(() => setOpen(false), [setOpen]);

  return (
    <AutoCompleteCell
      {...(props as unknown as CellProps<TDoc, V>)}
      value={props.value || null}
      fullWidth
      openOnFocus={false}
      loading={loading}
      filterOptions={filterOptions}
      getOptionKey={(option: string | TDoc | V) => (typeof option === 'string' ? option : String(getOptionKey(option)))}
      // While the user types, MUI compares the hits against the typed text; consumers only handle docs and values.
      isOptionEqualToValue={(option: string | TDoc | V, value: string | TDoc | V) =>
        typeof option === 'string' || typeof value === 'string'
          ? option === value
          : props.isOptionEqualToValue(option, value)
      }
      options={options}
      noOptionsText={stateText}
      preventOpen
      open={open}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onOpen={handleOpen}
      onClose={handleClose}
      defaultInputValue={defaultSearchText}
      textFieldProps={{
        sx: { border: 0, '& .MuiInputBase-input': { fontSize: '14px' } },
        fullWidth: true,
        onChange: (event) => handleSearch(event.target.value),
        onBlur: handleBlur,
        ...props.textFieldProps,
      }}
      onChange={handleChange as unknown as CellProps<TDoc, V>['onChange']}
      transform={undefined}
    />
  );
}
